import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
// import { MustMatch } from 'src/app/helpers/must-match-validator.directive';
// import { DateFormat, DateMin } from '../app/helpers/date-validator.directive';
import { ActivatedRoute, Router, NavigationEnd, RoutesRecognized } from '@angular/router';
import { filter, map, pairwise } from 'rxjs/operators';
import { Location } from '@angular/common';
import { fakeAsync } from '@angular/core/testing';
import { BehaviorSubject, Observable } from 'rxjs';
import { PortalService } from './portal.service';
import { environment } from '../environments/environment';

interface DrugDetails {
	drugName: string | null;
	drugID: number | null;
}

const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;
const emailRegex = /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/;
const zipRegex = /^\d{5}$/;

@Injectable({
	providedIn: 'root',
})

export class EnrollmentFormService {
	public enrollmentForm: FormGroup;
	public eRxEnrollment: boolean = false;
	public previousRoute: string;
	public currentRoute: string;
	public step: number = 1;
	public demoMode: boolean = false;
	stepSequence: any = [];
	currentEnrollmentObject: any = {};

	private enrollmentIDSubject = new BehaviorSubject<number | null>(null);
	enrollmentID$ = this.enrollmentIDSubject.asObservable();
	private drugDetailsSubject = new BehaviorSubject<DrugDetails>({
		drugName: null,
		drugID: null,
	});

	drugDetails$: Observable<{ drugName: string | null; drugID: number | null }> = this.drugDetailsSubject.asObservable();
	private patientAccountId: number | null = null;
	constructor(
		public fb: FormBuilder,
		private route: ActivatedRoute,
		private router: Router,
		private location: Location,
		private portalService: PortalService
	) {
		this.enrollmentForm = this.createEnrollmentForm();
		// this.addInsuranceEntry({
		// 	insuranceTypeID: 0,
		// 	insuranceCompanyName: '',
		// 	memberID: 0,
		// });

		// this.addfinMediHardshipEntry({
		// 	finMediHardshipTypeID: 0
		// });

		this.portalService.getEnrollmentSteps(environment.programID)
			.pipe(
				map((steps) =>
					steps
						.filter((step) => step.enrollmentRoute !== 'N/A' && step.sequenceNumber !== 'N/A')
						.sort((a, b) => +a.sequenceNumber - +b.sequenceNumber)
				)
			)
			.subscribe((stepSequence) => {
				console.log(stepSequence);
				this.stepSequence = stepSequence;
			})
	}

	private createEnrollmentForm(): FormGroup {
		return this.fb.group({
			programId: [0, Validators.required], // Required field
			programURL: ['', [Validators.required, Validators.pattern('https?://.+')]], // Must be a valid URL
			drugName: ['', Validators.required], // Required field
			lastName: ['', [Validators.required, Validators.maxLength(50)]], // Required and max length
			firstName: ['', [Validators.required, Validators.maxLength(50)]], // Required and max length
			middleName: ['', Validators.maxLength(50)], // Optional with max length
			birthdate: ['', Validators.required], // Required
			gender: ['', Validators.required], // Required
			phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]], // 10-digit phone number
			primaryPhoneTypeId: [''],
			altPhone: ['', Validators.pattern(/^\d{10}$/)], // Optional 10-digit phone number
			alternatePhoneTypeId: [''],
			email: ['', [Validators.required, Validators.email]], // Valid email
			acceptedTermsOfUse: [null, Validators.requiredTrue], // Must be true
			acceptedPrivacyRules: [null, Validators.requiredTrue], // Must be true
			acceptedBrandMedication: [null],
			acceptedMarketingAcknowledgment: [''],
			pharmacyPatientID: [0],
			pharmacyFamilyID: [0],
			sendEnrollmentNotification: [false],
			shippingAddress1: ['', Validators.required], // Required
			shippingAddress2: [''],
			shippingCity: ['', Validators.required], // Required
			shippingState: ['', Validators.required], // Required
			shippingZip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]], // 5-digit ZIP code
			billingAddress1: ['', Validators.required], // Required
			billingAddress2: [''],
			billingCity: ['', Validators.required], // Required
			billingState: ['', Validators.required], // Required
			billingZip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]], // 5-digit ZIP code
			physicianLastName: ['', Validators.maxLength(50)], // Optional
			physicianFirstName: ['', Validators.maxLength(50)], // Optional
			physicianPhone: ['', Validators.pattern(/^\d{10}$/)], // Optional 10-digit phone number
			physicianFax: ['', [Validators.pattern(/^\d{10}$/)]],
			lastEnrollmentStep: [0],
			shippingAddressType: [0],
			enrollmentSavedEventPublished: [false],
			drugID: [0],
			usCitizen: ['', Validators.required], // Required
			isInsuranceSelected: ['', Validators.required],
			isMedicarePartBCoverage: [false],
			isMedicarePartDCoverage: [false],
			physicianEmail: ['', Validators.email], // Optional valid email
			physicianAddress1: [''],
			physicianAddress2: [''],
			physicianCity: [''],
			physicianState: [''],
			physicianZip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
			acceptedDeclarationandAuthorization: [false, Validators.requiredTrue], // Must be true
			patientAccountId: ['', Validators.required], // Required
			submissionDate: [null],
			enrollmentId: [0],
			finMediHardshipDTOs: this.fb.array([]),
			enrollmentInsuranceDTOs: this.fb.array([]),
			enrollmentEligibilityDTO: this.fb.group({
				residency: [false, Validators.required], // Required
				income: [0, [Validators.min(0), Validators.maxLength(10)]], // Non-negative number
				household: [0, [Validators.min(1), Validators.max(99)]], // Must be at least 1
				patientDeclarations: [false, Validators.requiredTrue], // Must be true
				patientAuthorization: [false, Validators.requiredTrue], // Must be true
				incomeVerificationOption: [null],
				incomeVerificationAcknowledged: [false],
			}),
			contactMethodId: [0],
			isMavenDriven: [true],
			isMultitenancySupported: [true],
			status: [0],
			updateUTC: [null],
			processDurationDate: [null],
			caseId: [''],
		});
	}

	patchEnrollmentForm(data: any) {
		this.currentEnrollmentObject = JSON.parse(JSON.stringify(data));
		this.enrollmentForm.patchValue({
			patientAccountId: data.patientAccountId,
			enrollmentId: data.enrollmentId,
			drugName: data.drugName || "",
			drugID: data.drugID || "",
			programURL: data.programURL,
			programId: data.programId,
			lastEnrollmentStep: data.lastEnrollmentStep,

			//patient profile
			firstName: data.firstName,
			lastName: data.lastName,
			birthdate: data.birthdate,
			email: data.email,
			phone: data.phoneNumber || data.phone,
			primaryPhoneTypeId: data.primaryPhoneTypeId,
			altPhone: data.altPhone,
			alternatePhoneTypeId: data.alternatePhoneTypeId,
			billingAddress1: data.billingAddress1,
			billingAddress2: data.billingAddress2,
			billingCity: data.billingCity,
			billingState: data.billingState,
			billingZip: data.billingZip,
			shippingAddress1: data.shippingAddress1,
			shippingAddress2: data.shippingAddress2,
			shippingCity: data.shippingCity,
			shippingState: data.shippingState,
			shippingZip: data.shippingZip,
			sameAsHomeAddress: [false],
			gender: data.gender,
			usCitizen: data.usCitizen,

			//Physician
			physicianFirstName: data.physicianFirstName,
			physicianLastName: data.physicianLastName,
			physicianPhone: data.physicianPhone,
			physicianEmail: data.physicianEmail,
			physicianFax: data.physicianFax,
			physicianAddress1: data.physicianAddress1,
			physicianAddress2: data.physicianAddress2,
			physicianCity: data.physicianCity,
			physicianState: data.physicianState,
			physicianZip: data.physicianZip,

			//Insurance
			isMedicarePartBCoverage: data.isMedicarePartBCoverage,
			isMedicarePartDCoverage: data.isMedicarePartDCoverage,
			isInsuranceSelected: data.isInsuranceSelected,
		});

		// Clears Insurance info on patch eliminates duplicates
		this.enrollmentInsuranceDTOs.clear();
		this.finMediHardshipDTOs.clear();
		data.enrollmentInsuranceViewDTOs.forEach((insurance: any) => {
			this.addInsuranceEntry({
				insuranceTypeID: insurance.insuranceTypeID,
				insuranceCompanyName: insurance.insuranceCompanyName,
				memberID: insurance.insuranceInsuredID,
			});
		});
		data.finMediHardshipViewDTOs.forEach((finMediHardship: any) => {
			this.addfinMediHardshipEntry({
				finMediHardshipTypeID: finMediHardship.finMediHardshipTypeID
			});
		});

		this.enrollmentEligibilityDTO.patchValue({
			residency: data.enrollmentEligibilityViewDTOs.residency,
			income: data.enrollmentEligibilityViewDTOs.income,
			household: data.enrollmentEligibilityViewDTOs.household,
			patientDeclarations: data.enrollmentEligibilityViewDTOs.patientDeclarations,
			patientAuthorization: data.enrollmentEligibilityViewDTOs.patientAuthorization,
			incomeVerificationOption: data.enrollmentEligibilityViewDTOs.incomeVerificationOption,
			incomeVerificationAcknowledged: data.enrollmentEligibilityViewDTOs.incomeVerificationAcknowledged,
		})
	}

	// Reset form to default values
	resetForm(): void {
		this.enrollmentForm = this.createEnrollmentForm();
		this.patientAccountId = null;
		this.currentEnrollmentObject = {};
		this.enrollmentIDSubject.next(null);
		this.drugDetailsSubject.next({ drugName: null, drugID: null });
	}

	// Handling page refresh on any enrollment step
	checkAndfetchEnrollmentDetails(enrollmentId: any) {
		if (!Object.keys(this.currentEnrollmentObject).length) {
			this.portalService.getEnrollmentById(null, enrollmentId).subscribe((res) => {
				console.log("FROM SERVICE", res);
				this.patchEnrollmentForm(res);
			});
		}
	}

	setPatientAccountId(id: number): void {
		this.patientAccountId = id;
	}

	getPatientAccountId(): number | null {
		return this.patientAccountId;
	}

	setEnrollmentID(id: number): void {
		this.enrollmentIDSubject.next(id);
	}

	getEnrollmentID(): number | null {
		return this.enrollmentIDSubject.value;
	}

	setDrugDetails(drugName: string | null, drugID: number | null): void {
		this.drugDetailsSubject.next({ drugName, drugID });
	}

	getDrugDetails(): DrugDetails {
		return this.drugDetailsSubject.value;
	}

	addInsuranceEntry(data: { insuranceTypeID: number; insuranceCompanyName: string; memberID: number }): void {
		const otherInsuranceTypeID = 10012;
		if (data.insuranceTypeID === 0) {
			console.warn('Invalid insuranceTypeID: 0. Entry not added.');
			return;
		} else {

			let insuranceGroup: FormGroup;
			if (otherInsuranceTypeID === data.insuranceTypeID) {
				insuranceGroup = this.fb.group({
					insuranceTypeID: [data.insuranceTypeID, Validators.required],
					insuranceCompanyName: [data.insuranceCompanyName, Validators.required],
					memberID: [data.memberID, Validators.required],
				});
			} else {
				insuranceGroup = this.fb.group({
					insuranceTypeID: [data.insuranceTypeID, Validators.required],
				});
			}

			this.enrollmentInsuranceDTOs.push(insuranceGroup);
		}

	}

	get enrollmentInsuranceDTOs(): FormArray {
		return this.enrollmentForm.get('enrollmentInsuranceDTOs') as FormArray;
	}

	removeInsuranceEntry(index: number): void {
		this.enrollmentInsuranceDTOs.removeAt(index);
	}

	addfinMediHardshipEntry(data: { finMediHardshipTypeID: number }): void {
		if (data.finMediHardshipTypeID === 0) {
			console.warn('Invalid insuranceTypeID: 0. Operation skipped.');
			return;
		} else {
			const insuranceHardshipGroup = this.fb.group({
				finMediHardshipTypeID: [data.finMediHardshipTypeID, Validators.required],
			});

			this.finMediHardshipDTOs.push(insuranceHardshipGroup);
		}
	}

	get finMediHardshipDTOs(): FormArray {
		return this.enrollmentForm.get('finMediHardshipDTOs') as FormArray;
	}

	removefinMediHardshipDTOs(index: number): void {
		this.finMediHardshipDTOs.removeAt(index);
	}

	get enrollmentEligibilityDTO(): FormGroup {
		return this.enrollmentForm.get('enrollmentEligibilityDTO') as FormGroup;
	}

	getRouteFromSequence(sequence: string) {
		const routeExist = this.stepSequence.find((step: any) => +step.sequenceNumber === (+sequence !== 0 ? +sequence : 1));
		return routeExist ? routeExist : this.stepSequence[0];
	}

	getNextStep() {
		let currentStep = localStorage.getItem('CurrentEnrollmentStep') !== '0' ? localStorage.getItem('CurrentEnrollmentStep') : '1';
		return this.getRouteFromSequence((+currentStep + 1).toString());
	}
}
